import { EmptyState, ErrorState } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'

/**
 * The index's states compose the design-system {@link EmptyState} and {@link ErrorState}:
 * they own the frame, the centring and the first-run/filtered/failed distinction, while
 * the display-scale heading below is this surface's own emphasis.
 */
const HEADING = 'font-ds-display text-ds-xl text-ds-text'
const BODY = 'mx-auto block max-w-md text-ds-sm text-ds-text-muted'

/**
 * Nothing exists yet. An invitation, not a report — the only useful thing to do
 * here is write the first article.
 */
export function EmptyCorpusState({ onCreate }: { onCreate: () => void }) {
	return (
		<EmptyState
			className="p-10"
			title={<span className={HEADING}>No articles yet</span>}
			description={
				<span className={BODY}>
					An article is long-form editorial content — a brand's voice, a market brief, a product story — classified on
					the same axes as every other object.
				</span>
			}
			action={
				<Button className="rounded-ds-full px-5" onPress={onCreate}>
					New article
				</Button>
			}
		/>
	)
}

/**
 * Articles exist, but the active narrowing excludes all of them.
 *
 * Deliberately NOT the same state as an empty corpus: the API answers both with one
 * 204, so telling them apart is the index's job, and conflating them would tell an
 * author to write an article they have already written.
 */
export function FilteredEmptyState({
	activeFilters,
	onClear,
}: {
	/** What is currently narrowing the list, in words. */
	activeFilters: string[]
	onClear: () => void
}) {
	return (
		<EmptyState
			className="p-10"
			variant="filtered"
			title={<span className={HEADING}>No articles match these filters</span>}
			description={<span className={BODY}>Narrowed by {activeFilters.join(', ')}. Articles exist outside that.</span>}
			action={
				<Button variant="secondary" className="rounded-ds-full px-5" onPress={onClear}>
					Clear filters
				</Button>
			}
		/>
	)
}

/** The list could not be read. Distinct from either empty state: nothing is known. */
export function ArticleErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
	return (
		<ErrorState
			className="p-10"
			title={<span className={HEADING}>Could not load articles</span>}
			description={<span className={BODY}>{message}</span>}
			action={
				<Button variant="secondary" className="rounded-ds-full px-5" onPress={onRetry}>
					Try again
				</Button>
			}
		/>
	)
}
